/**
 * Energy Certificate Simulator - Energy Class Thresholds
 *
 * Energy class definitions based on EP indicator [kWh/m²·year]
 * for residential buildings according to Polish regulations.
 */

import type { EnergyClass, BuildingType } from '../types';

// =============================================================================
// ENERGY CLASS THRESHOLDS
// =============================================================================

/**
 * EP thresholds for residential buildings [kWh/m²·year]
 * Source: Polish energy certification methodology
 */
export const ENERGY_CLASS_THRESHOLDS: Record<EnergyClass, { min: number; max: number }> = {
  'A+': { min: -Infinity, max: 0 },   // Plus-energy building
  A: { min: 0, max: 63 },              // Very low energy
  B: { min: 63, max: 157 },            // Low energy
  C: { min: 157, max: 250 },           // Standard (new buildings)
  D: { min: 250, max: 344 },           // Elevated consumption
  E: { min: 344, max: 438 },           // High consumption
  F: { min: 438, max: 531 },           // Very high consumption
  G: { min: 531, max: Infinity },      // "Energy vampire"
};

/**
 * Determine energy class from EP value
 */
export function determineEnergyClass(epValue: number): EnergyClass {
  if (epValue < 0) return 'A+';
  if (epValue < 63) return 'A';
  if (epValue < 157) return 'B';
  if (epValue < 250) return 'C';
  if (epValue < 344) return 'D';
  if (epValue < 438) return 'E';
  if (epValue < 531) return 'F';
  return 'G';
}

// =============================================================================
// WT 2021 EP LIMITS
// =============================================================================

/**
 * Maximum EP values for new buildings according to WT 2021 [kWh/m²·year]
 */
export const WT_2021_EP_LIMITS: Record<BuildingType, number> = {
  detached: 70,
  semi_detached: 65,
  terraced: 65,
  apartment: 65,
};

/**
 * Get EP limit for building type
 */
export function getEPLimit(buildingType: BuildingType): number {
  return WT_2021_EP_LIMITS[buildingType] ?? 70;
}

/**
 * Check if EP exceeds WT 2021 limit
 */
export function exceedsEPLimit(epValue: number, buildingType: BuildingType): boolean {
  return epValue > getEPLimit(buildingType);
}

// =============================================================================
// ENERGY CLASS METADATA
// =============================================================================

/**
 * Energy class colors for visualization
 */
export const ENERGY_CLASS_COLORS: Record<EnergyClass, string> = {
  'A+': '#00843D', // Dark green
  A: '#2FAC66',    // Green
  B: '#90C31F',    // Light green
  C: '#FFED00',    // Yellow
  D: '#FFB819',    // Orange
  E: '#F36F21',    // Dark orange
  F: '#E31C23',    // Red
  G: '#8B0000',    // Dark red
};

/**
 * Energy class descriptions in Polish
 */
export const ENERGY_CLASS_DESCRIPTIONS: Record<EnergyClass, string> = {
  'A+': 'Budynek plusenergetyczny - produkuje więcej energii niż zużywa',
  A: 'Budynek bezemisyjny o bardzo niskim zużyciu energii',
  B: 'Budynek energooszczędny o niskim zużyciu energii',
  C: 'Budynek o średnim zużyciu energii (standard dla nowych budynków)',
  D: 'Budynek o podwyższonym zużyciu energii',
  E: 'Budynek o znacznym zużyciu energii',
  F: 'Budynek o wysokim zużyciu energii',
  G: 'Budynek o bardzo wysokim zużyciu energii ("wampir energetyczny")',
};

/**
 * Energy class labels in Polish
 */
export const ENERGY_CLASS_LABELS: Record<EnergyClass, string> = {
  'A+': 'Klasa A+ (Plusenergetyczny)',
  A: 'Klasa A (Bezemisyjny)',
  B: 'Klasa B (Energooszczędny)',
  C: 'Klasa C (Standard)',
  D: 'Klasa D (Podwyższone zużycie)',
  E: 'Klasa E (Znaczne zużycie)',
  F: 'Klasa F (Wysokie zużycie)',
  G: 'Klasa G (Bardzo wysokie zużycie)',
};

// =============================================================================
// REFERENCE VALUES
// =============================================================================

/**
 * Typical EP values for reference [kWh/m²·year]
 */
export const REFERENCE_EP_VALUES = {
  /** Passive house standard */
  passiveHouse: 15,
  /** Low-energy building */
  lowEnergy: 50,
  /** WT 2021 limit (single-family) */
  wt2021: 70,
  /** Old building (before 1990) */
  oldBuilding: 200,
  /** Very old building (before 1960) */
  veryOldBuilding: 350,
  /** Pre-war building without insulation */
  preWarNoInsulation: 450,
} as const;

/**
 * Polish average EP for residential buildings
 */
export const POLISH_AVERAGE_EP = 160; // kWh/m²·year
